/**
 * LiveStatusButton — bouton Live teinté selon l'état réel du capteur (G3)
 *
 * Le statut est calculé côté serveur (GET /api/horses → horse.live) : le
 * composant ne fait que le restituer. Fond pâle de la couleur d'état, texte
 * et bordure en teinte foncée (≥ 6,3:1 dans les deux thèmes), pastille
 * pleine décorative. La couleur n'est jamais le seul porteur de l'info :
 * la légende visible sous le nom (lisible au toucher, sans survol) et
 * l'aria-label donnent le statut en toutes lettres.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Radio } from 'lucide-react';
import { BTN } from '../constants/theme';
import { semanticColors, semanticColorsDark } from '../constants/semanticColors';
import { formatRelativeTime } from '../utils/relativeTime';

const KNOWN_STATUSES = ['ok', 'late', 'offline', 'never', 'unknown'];

// offline / never / unknown partagent le style neutre (G8) : aucun signal
// exploitable, seul le libellé les distingue.
function getLiveTone(status, darkMode) {
  const c = darkMode ? semanticColorsDark : semanticColors;
  if (status === 'ok') {
    return { bg: c.greenBg, text: darkMode ? c.greenText : c.greenTextStrong, dot: c.greenSolid };
  }
  if (status === 'late') {
    return { bg: c.orangeBg, text: darkMode ? c.orangeText : c.orangeTextStrong, dot: c.orangeSolid };
  }
  return { bg: c.neutralBg, text: c.neutralText, dot: c.neutralSolid };
}

// Un cheval fraîchement créé côté client n'a pas encore de `live` : inconnu,
// jamais vert par défaut.
const statusOf = (live) => (KNOWN_STATUSES.includes(live?.status) ? live.status : 'unknown');

/** Textes du statut : libellé court + résumé « EkynAir Live · statut · dernière donnée ». */
export function buildLiveTexts(live, tr, language, now = Date.now()) {
  const status = statusOf(live);
  const statusLabel = tr(`horses.live.status.${status}`);
  const relative = status === 'unknown' ? null : formatRelativeTime(live?.lastAt, language, now);
  let lastData = tr('horses.live.noData');
  if (status === 'unknown') lastData = tr('horses.live.unavailable');
  else if (relative) lastData = tr('horses.live.lastData', { time: relative });
  return {
    status,
    statusLabel,
    lastData,
    summary: tr('horses.live.summary', { product: tr('horses.live.product'), status: statusLabel, lastData }),
  };
}

const Dot = ({ color }) => (
  <span
    aria-hidden="true"
    style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }}
  />
);

/** Légende visible sous le nom du cheval (statut lisible sans survol). */
export const LiveStatusLegend = ({ live, darkMode }) => {
  const { t: tr, i18n } = useTranslation();
  const { statusLabel, lastData } = buildLiveTexts(live, tr, i18n.language);
  const tone = getLiveTone(statusOf(live), darkMode);
  return (
    <p
      data-testid="live-status-legend"
      style={{ color: tone.text, fontSize: '13px', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}
    >
      <Dot color={tone.dot} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {tr('horses.live.label')} · {statusLabel} · {lastData}
      </span>
    </p>
  );
};

const LiveStatusButton = ({ live, darkMode, onClick }) => {
  const { t: tr, i18n } = useTranslation();
  const { status, summary } = buildLiveTexts(live, tr, i18n.language);
  const tone = getLiveTone(status, darkMode);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={summary}
      title={summary}
      data-live-status={status}
      style={{
        ...BTN.secondary(darkMode),
        width: '100%',
        minHeight: '44px',
        justifyContent: 'center',
        background: tone.bg,
        color: tone.text,
        border: `1.5px solid ${tone.text}`,
      }}
    >
      <Dot color={tone.dot} />
      <Radio size={15} aria-hidden="true" /> {tr('horses.live.label')}
    </button>
  );
};

export default LiveStatusButton;
